import React, { useState, useEffect, useCallback, useRef } from 'react';
import QrCodePage from '../Common/QrCode';
import { useConnection } from '../../hooks/useConnection';
import { useStatusPolling } from '../../hooks/useStatusPolling';
import { useConnectionState, useToastState } from '../../store/useAppStore';

interface QRCodeStateProps {
  qrCode: string;
  onRetry?: () => void;
}

export const QRCodeState: React.FC<QRCodeStateProps> = ({ qrCode, onRetry }) => {
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  const [showExpired, setShowExpired] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const { connectionInfo, fetchQRCodeFallback } = useConnection();
  const { isConnected } = useConnectionState();
  const { showToast } = useToastState();

  // Use the new status polling hook - keep polling even if QR expires
  const {
    isPolling,
    pollingAttempts,
    maxAttempts,
    resetPolling
  } = useStatusPolling({
    interval: 8000, // Check every 8 seconds (faster)
    maxAttempts: 45, // 6 minutes of polling (longer)
    enabled: !isConnected, // Keep polling until connected, regardless of QR status
    onConnectionDetected: () => {
      console.log('🎉 Auto-polling detected connection!');
      showToast({
        type: 'success',
        title: 'Conectado Automaticamente',
        message: 'WhatsApp conectado! Redirecionando...',
        duration: 3000
      });
      
      // Redirect after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error) => {
      // Only log if it's not a network/timeout error to reduce noise
      if (!error.message.includes('Failed to fetch') && !error.message.includes('timeout')) {
        console.log('Auto-polling error:', error.message);
      }
    }
  });

  // Timer effect
  useEffect(() => {
    if (timeLeft <= 0) {
      setShowExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleValidateConnection = async () => {
    setIsValidating(true);
    
    try {
      // Check if already connected
      if (isConnected) {
        showToast({
          type: 'success',
          title: 'Já Conectado',
          message: 'WhatsApp já está conectado! Redirecionando...',
          duration: 3000
        });
        // Let the parent component handle the navigation
        window.location.reload();
        return;
      }

      // Check backend connection status using authId
      const authId = localStorage.getItem('wpp_auth_uuid');
      if (!authId) {
        throw new Error('AuthId não encontrado');
      }

      // Use exponential backoff for status check
      const checkStatus = async () => {
        const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/status`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 30000;
          console.log(`⏳ Status check rate limited, waiting ${waitTime}ms`);
          throw new Error(`Rate limited (429), waiting ${waitTime}ms`);
        }

        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }

        return response.json();
      };

      // Retry status check with exponential backoff
      let attempt = 0;
      const maxRetries = 2;
      let lastError: Error;

      while (attempt <= maxRetries) {
        try {
          const data = await checkStatus();
          
          if (data.success && data.data?.status === 'connected') {
            showToast({
              type: 'success',
              title: 'Conexão Detectada',
              message: 'WhatsApp conectado! Atualizando interface...',
              duration: 3000
            });
            window.location.reload();
            return;
          } else {
            // Try to refresh QR code
            await fetchQRCodeFallback();
            showToast({
              type: 'info',
              title: 'Status Verificado',
              message: 'Ainda não conectado. QR Code atualizado.',
              duration: 4000
            });
            return;
          }
        } catch (error) {
          lastError = error as Error;
          
          if (attempt === maxRetries) {
            break;
          }
          
          // Wait before retry
          const delay = Math.min(1000 * Math.pow(2, attempt) + Math.random() * 500, 5000);
          console.log(`⏳ Retrying status check in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          attempt++;
        }
      }

      // If we get here, all retries failed
      throw lastError!;
    } catch (error) {
      console.error('Validation error:', error);
      showToast({
        type: 'error',
        title: 'Erro na Validação',
        message: 'Não foi possível verificar o status da conexão.',
        duration: 5000
      });
    } finally {
      setIsValidating(false);
    }
  };

  if (showExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center wa-animate-fadeIn" 
           style={{ 
             background: 'linear-gradient(135deg, var(--wa-yellow-50), var(--wa-warning-50))',
             padding: 'var(--wa-space-4)' 
           }}>
        <div className="wa-card wa-card-body text-center" style={{ maxWidth: '28rem', width: '100%' }}>
          <div style={{ marginBottom: 'var(--wa-space-6)' }}>
            <div 
              className="flex items-center justify-center mx-auto wa-gradient-yellow-primary"
              style={{ 
                width: 'var(--wa-space-20)', 
                height: 'var(--wa-space-20)', 
                borderRadius: 'var(--wa-rounded-full)',
                marginBottom: 'var(--wa-space-4)',
                border: '2px solid var(--wa-yellow-400)'
              }}
            >
              <span style={{ fontSize: 'var(--wa-text-3xl)', color: 'var(--wa-yellow-900)' }}>⏰</span>
            </div>
            <h2 className="wa-heading-4" style={{ marginBottom: 'var(--wa-space-2)' }}>
              QR Code Expirado
            </h2>
            <p className="wa-body-small">
              O QR Code expirou. Valide a conexão ou gere um novo código.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-3)' }}>
            <button
              onClick={handleValidateConnection}
              disabled={isValidating}
              className="wa-btn wa-focus-ring"
              style={{ 
                width: '100%',
                background: 'var(--wa-gradient-yellow-primary)',
                color: 'var(--wa-yellow-900)',
                border: '2px solid var(--wa-yellow-400)',
                fontWeight: 'var(--wa-font-semibold)',
                opacity: isValidating ? 0.7 : 1,
                cursor: isValidating ? 'wait' : 'pointer'
              }}
            >
              <span aria-hidden="true">{isValidating ? '⏳' : '✅'}</span> 
              {isValidating ? 'Validando...' : 'Validar Conexão'}
            </button>

            <button
              onClick={onRetry}
              className="wa-btn wa-btn-outline wa-btn-lg"
              style={{ width: '100%' }}
            >
              🔄 Gerar Novo QR Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main 
      className="min-h-screen flex items-center justify-center wa-gradient-bg wa-animate-fadeIn" 
      style={{ padding: 'var(--wa-space-4)' }}
      role="main"
      aria-label="Conectar WhatsApp via QR Code"
    >
      <section 
        className="wa-card wa-card-body text-center" 
        style={{ maxWidth: '32rem', width: '100%' }}
        aria-labelledby="qr-title"
      >
        {/* Header */}
        <header style={{ marginBottom: 'var(--wa-space-8)' }}>
          <div 
            className="wa-gradient-yellow-primary flex items-center justify-center mx-auto wa-hover-scale"
            style={{ 
              width: 'var(--wa-space-16)', 
              height: 'var(--wa-space-16)', 
              borderRadius: 'var(--wa-rounded-full)',
              marginBottom: 'var(--wa-space-4)',
              cursor: 'pointer',
              border: '2px solid var(--wa-yellow-400)',
              boxShadow: '0 4px 12px rgba(255, 193, 7, 0.25)'
            }}
            role="img"
            aria-label="Ícone do WhatsApp"
          >
            <span style={{ fontSize: 'var(--wa-text-2xl)', color: 'var(--wa-yellow-900)' }} aria-hidden="true">📱</span>
          </div>
          <h1 id="qr-title" className="wa-heading-3" style={{ marginBottom: 'var(--wa-space-2)' }}>
            Conectar WhatsApp
          </h1>
          <p className="wa-body-small">
            Escaneie o QR Code com seu celular
          </p>
        </header>

        {/* QR Code */}
        <section style={{ marginBottom: 'var(--wa-space-8)' }} aria-labelledby="qr-section">
          <h2 id="qr-section" className="wa-caption" style={{ marginBottom: 'var(--wa-space-3)', fontWeight: 'var(--wa-font-semibold)' }}>
            QR Code para conexão
          </h2>
          <div 
            className="wa-interactive wa-focus-ring"
            style={{ 
              backgroundColor: 'var(--wa-gray-50)',
              borderRadius: 'var(--wa-rounded-2xl)',
              padding: 'var(--wa-space-6)',
              border: `2px dashed var(--wa-gray-200)`,
              transition: 'var(--wa-transition-hover)'
            }}
            role="img"
            aria-label="QR Code para conectar WhatsApp"
            tabIndex={0}
          >
            <QrCodePage qrCode={qrCode} />
          </div>
        </section>

        {/* Timer and Auto-validation Status */}
        <div style={{ marginBottom: 'var(--wa-space-6)' }} role="status" aria-live="polite">
          <div className="flex items-center justify-center wa-body-small" style={{ gap: 'var(--wa-space-2)', color: 'var(--wa-gray-600)' }}>
            <div 
              className="wa-animate-pulse"
              style={{ 
                width: 'var(--wa-space-2)', 
                height: 'var(--wa-space-2)', 
                backgroundColor: isPolling ? 'var(--wa-success-500)' : 'var(--wa-yellow-500)',
                borderRadius: 'var(--wa-rounded-full)'
              }}
              aria-hidden="true"
            ></div>
            <span>
              Expira em: <strong style={{ color: 'var(--wa-yellow-700)' }}>{formatTime(timeLeft)}</strong>
            </span>
          </div>
          
          {/* Auto-polling indicator */}
          {isPolling && (
            <div className="flex items-center justify-center wa-caption" style={{ 
              gap: 'var(--wa-space-1)', 
              color: 'var(--wa-success-600)',
              marginTop: 'var(--wa-space-2)'
            }}>
              <span>🔄</span>
              <span>Verificando conexão automaticamente...</span>
            </div>
          )}
          
          {pollingAttempts > 0 && !isPolling && (
            <div className="flex items-center justify-center wa-caption" style={{ 
              gap: 'var(--wa-space-1)', 
              color: 'var(--wa-gray-500)',
              marginTop: 'var(--wa-space-2)'
            }}>
              <span>👁️</span>
              <span>Monitorou conexão ({pollingAttempts}/{maxAttempts} tentativas)</span>
            </div>
          )}
        </div>

        {/* Instructions */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-4)' }} aria-labelledby="instructions-title">
          <div 
            style={{ 
              backgroundColor: 'var(--wa-info-50)',
              borderRadius: 'var(--wa-rounded-xl)',
              padding: 'var(--wa-space-4)'
            }}
          >
            <h3 id="instructions-title" className="wa-body-small" style={{ fontWeight: 'var(--wa-font-semibold)', color: 'var(--wa-info-800)', marginBottom: 'var(--wa-space-3)' }}>
              <span aria-hidden="true">📋</span> Como conectar:
            </h3>
            <ol 
              className="wa-caption" 
              style={{ 
                textAlign: 'left', 
                color: 'var(--wa-info-700)', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 'var(--wa-space-2)',
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}
            >
              <li className="flex" style={{ gap: 'var(--wa-space-2)' }}>
                <span style={{ fontWeight: 'var(--wa-font-bold)', minWidth: '1.2em' }} aria-label="Passo 1">1.</span>
                <span>Abra o WhatsApp no seu celular</span>
              </li>
              <li className="flex" style={{ gap: 'var(--wa-space-2)' }}>
                <span style={{ fontWeight: 'var(--wa-font-bold)', minWidth: '1.2em' }} aria-label="Passo 2">2.</span>
                <span>Toque no menu (três pontos) e selecione "Dispositivos conectados"</span>
              </li>
              <li className="flex" style={{ gap: 'var(--wa-space-2)' }}>
                <span style={{ fontWeight: 'var(--wa-font-bold)', minWidth: '1.2em' }} aria-label="Passo 3">3.</span>
                <span>Toque em "Conectar um dispositivo"</span>
              </li>
              <li className="flex" style={{ gap: 'var(--wa-space-2)' }}>
                <span style={{ fontWeight: 'var(--wa-font-bold)', minWidth: '1.2em' }} aria-label="Passo 4">4.</span>
                <span>Escaneie este QR Code com a câmera</span>
              </li>
            </ol>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-3)' }}>
            {/* Validation button */}
            <button
              onClick={handleValidateConnection}
              disabled={isValidating}
              className="wa-btn wa-focus-ring"
              style={{ 
                width: '100%',
                background: 'var(--wa-gradient-yellow-primary)',
                color: 'var(--wa-yellow-900)',
                border: '2px solid var(--wa-yellow-400)',
                fontWeight: 'var(--wa-font-semibold)',
                opacity: isValidating ? 0.7 : 1,
                cursor: isValidating ? 'wait' : 'pointer'
              }}
              aria-label="Validar status da conexão com WhatsApp"
            >
              <span aria-hidden="true">{isValidating ? '⏳' : '✅'}</span> 
              {isValidating ? 'Validando Conexão...' : 'Validar Conexão'}
            </button>

            {/* Retry button */}
            {onRetry && (
              <button
                onClick={onRetry}
                className="wa-btn wa-btn-outline wa-focus-ring"
                style={{ width: '100%' }}
                aria-label="Gerar novo QR Code para conexão"
              >
                <span aria-hidden="true">🔄</span> Gerar novo QR Code
              </button>
            )}
          </div>
        </section>
      </section>
    </main>
  );
};